import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import CollectorPage from './pages/CollectorPage';
import DashboardPage from './pages/DashboardPage';
import ExpertDashboard from './pages/ExpertDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AccountPage from './pages/AccountPage';
import AuthPage from './pages/AuthPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AnalystLoginPage from './pages/AnalystLoginPage';
import AnalystDashboard from './pages/AnalystDashboard';
import ChiffreurLoginPage from './pages/ChiffreurLoginPage';
import ChiffreurDashboard from './pages/ChiffreurDashboard';
import PartnerPage from './pages/PartnerPage';
import AffiliateDashboard from './pages/AffiliateDashboard';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import EspaceProPage from './pages/EspaceProPage';
import ReseauPage from './pages/ReseauPage';
import ReseauLeadCapturePage from './pages/ReseauLeadCapturePage';
import ReseauInscriptionPage from './pages/ReseauInscriptionPage';
import ReseauLoginPage from './pages/ReseauLoginPage';
import ReseauDashboard from './pages/ReseauDashboard';
import PartenaireLoginPage from './pages/PartenaireLoginPage';
import PartenaireDashboard from './pages/PartenaireDashboard';
import PartenairePaymentSuccessPage from './pages/PartenairePaymentSuccessPage';
import CommercialLoginPage from './pages/CommercialLoginPage';
import CommercialDashboard from './pages/CommercialDashboard';
import AdminResetPasswordPage from './pages/AdminResetPasswordPage';
import ProjectCompletionPage from './pages/ProjectCompletionPage';
// Radar Foncier Pages
import RadarFoncierPage from './pages/RadarFoncierPage';
import RadarConfirmationPage from './pages/RadarConfirmationPage';
import RadarSuiviPage from './pages/RadarSuiviPage';
import RadarPaiementDirectPage from './pages/RadarPaiementDirectPage';

// Conseil Stratégique Pages
import ConseilConfirmationPage from './pages/ConseilConfirmationPage';
import ConseilSuiviPage from './pages/ConseilSuiviPage';
import ConseilRdvPage from './pages/ConseilRdvPage';

// Protected Route Component
const ProtectedRoute = ({ children, requireExpert = false }) => {
  const { isAuthenticated, isExpert, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  if (requireExpert && !isExpert) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// App Routes
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<CollectorPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/espace-pro" element={<EspaceProPage />} />
      <Route path="/reseau" element={<ReseauLeadCapturePage />} />
      <Route path="/reseau/choisir" element={<ReseauPage />} />
      <Route path="/reseau/:type/inscription" element={<ReseauInscriptionPage />} />
      <Route path="/reseau/login" element={<ReseauLoginPage />} />
      <Route path="/reseau/:type/login" element={<ReseauLoginPage />} />
      <Route path="/reseau/dashboard" element={<ReseauDashboard />} />
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/analyst" element={<AnalystLoginPage />} />
      <Route path="/analyst/dashboard" element={<AnalystDashboard />} />
      <Route path="/chiffreur" element={<ChiffreurLoginPage />} />
      <Route path="/chiffreur/dashboard" element={<ChiffreurDashboard />} />
      <Route path="/p/:subdomain" element={<PartnerPage />} />
      <Route path="/p/:subdomain/:commercial_slug" element={<PartnerPage />} />
      <Route path="/affiliate/:code" element={<AffiliateDashboard />} />
      
      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/account" 
        element={
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/expert" 
        element={
          <ProtectedRoute requireExpert>
            <ExpertDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/dashboard" 
        element={
          <ProtectedRoute requireExpert>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Partenaire Routes */}
      <Route path="/partenaire/login" element={<PartenaireLoginPage />} />
      <Route path="/partenaire/dashboard" element={<PartenaireDashboard />} />
      <Route path="/partenaire/expert" element={<ExpertDashboard />} />
      <Route path="/partenaire/payment-success" element={<PartenairePaymentSuccessPage />} />
      {/* Commercial Routes */}
      <Route path="/commercial/login" element={<CommercialLoginPage />} />
      <Route path="/commercial/dashboard" element={<CommercialDashboard />} />
      <Route path="/complete-dossier/:token" element={<ProjectCompletionPage />} />
      
      {/* Admin Reset Password */}
      <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
      
      {/* Radar Foncier Routes */}
      <Route path="/radar" element={<RadarFoncierPage />} />
      <Route path="/radar/confirmation" element={<RadarConfirmationPage />} />
      <Route path="/radar/suivi" element={<RadarSuiviPage />} />
      <Route path="/radar/paiement-direct" element={<RadarPaiementDirectPage />} />
      
      {/* Conseil Stratégique Routes */}
      <Route path="/conseil/confirmation" element={<ConseilConfirmationPage />} />
      <Route path="/conseil/suivi" element={<ConseilSuiviPage />} />
      <Route path="/conseil/rdv" element={<ConseilRdvPage />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  // Empêche la molette de la souris de modifier accidentellement les inputs numériques
  // (bug classique : type="number" incrémente/décrémente au scroll)
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target && e.target.tagName === 'INPUT' && e.target.type === 'number' && document.activeElement === e.target) {
        e.target.blur();
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: true });
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Public Sans, sans-serif',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
